import React from 'react';
import { useEffect, useState } from 'react';
import { fetchUserInfo, canSharePhotos, downloadPhoto, sharePhoto } from '../../utils';
import Base from '../Base';
import Title from '../../components/common/Title';
import { useTranslation } from 'react-i18next';
import Card from '../../components/elements/Card';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import Modal from '../../components/elements/Modal';
import Button from '../../components/elements/Button';
import NotFound from '../../components/common/NotFound';

// The albums container is public; /album hands back blob names and we build the address from them,
// the same way the public gallery does.
const ALBUM_BASE = 'https://herculeanprod.blob.core.windows.net/albums';
const albumUrl = (name) => `${ALBUM_BASE}/${encodeURIComponent(name)}`;

const Albums = () => {
  const { t } = useTranslation();
  const [album, setAlbum] = useState(null);
  const photos = album?.photos;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // The PICTURE rather than its address: saving it and handing it to the share sheet both want its
  // blob name, so the file arrives called something better than "download".
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  // Whether a native share sheet is on offer is a property of the browser, not of the photograph —
  // so it is asked once, not per tile.
  const [shareable] = useState(canSharePhotos);

  useEffect(() => {
    fetchUserInfo('/album', setAlbum, setLoading, setError, []);
  }, []);

  const handleCloseModal = () => setSelected(null);

  // The photographer sells the pictures on his own site; an album without one has no button.
  const orderLink = album?.order_url && (
    <a
      href={album.order_url}
      target="_blank"
      rel="noopener noreferrer"
      className="btn btn-primary"
    >
      <Icon name="shopping-cart" theme="light" className="me-1" /> {t('orderYourPhotos')}
    </a>
  );

  // Fetching the full-size picture takes a moment on a phone, and a button that looks idle while it
  // works gets tapped again — which on the share path means two sheets.
  const run = async (action) => {
    if (busy || !selected) return;
    setBusy(true);
    try {
      await action(albumUrl(selected.name), selected.name);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Base activeNav="home">

        <Card>
        <Back to='/home' />
          <Title my='1' className='mt-3'><Icon name="picture" theme='primary' className='me-2' />{t('photos')}</Title>
          {orderLink && <div className="text-center mt-2">{orderLink}</div>}
        </Card>


        {photos?.length > 0 ? (
        <div className='row d-flex justify-content-center g-0'>
            {photos.map((picture) => (
                <div className='col d-flex justify-content-center mb-3' key={picture.name}>
                    <img
                    src={albumUrl(picture.name)}
                    alt={picture.name}
                    onClick={() => setSelected(picture)}
                    style={{
                        width: '11rem',
                        height: '11rem',
                        objectFit: 'cover',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        backgroundColor: 'white',
                    }}
                    className="outline-light"
                    />
                </div>
            ))}
            </div>
      ) : (
        <NotFound translationKey="noPhotosFound" loading={loading} />
      )}

      <Modal show={!!selected} title={t('photoPreview')} onClose={handleCloseModal} hideFooter={true}>
        {selected && (
          <div className="text-center">
            <img src={albumUrl(selected.name)} alt="Preview" className="img-fluid mb-3" style={{ maxHeight: '60vh' }} />
            <div className="d-flex justify-content-center flex-wrap gap-2">
              {/* Share first where it exists: on a phone this is the whole point — the picture goes
                  straight into WhatsApp or Instagram without a trip through the downloads folder.
                  Desktop browsers do not offer it and simply get the save button. */}
              {shareable && (
                <Button styles={{}} disabled={busy} onClick={() => run(sharePhoto)}>
                  <Icon name="share" theme="light" className="me-1" /> {t('share')}
                </Button>
              )}
              <Button styles={{}} theme={shareable ? 'secondary' : 'primary'} disabled={busy} onClick={() => run(downloadPhoto)}>
                <Icon name="download" theme="light" className="me-1" /> {t('download')}
              </Button>
            </div>
            {orderLink && <div className="mt-2">{orderLink}</div>}
          </div>
        )}
      </Modal>

    </Base>
  );
};

export default Albums;
